import { MultiSelectFilter } from "@/components/MultiSelectFilter";
import { useCatalogContext } from "@/hooks/useCatalog";
import { useCycleLeadCounts } from "@/hooks/useCycleLeadCounts";
import { useCrmStore } from "@/stores/crm.store";

// Sentinel store value for the "no cycle assigned" bucket (cycleId IS NULL).
const NO_CYCLE = "none";

/**
 * School-cycle filter for the Leads view, backed by the crm.store `cycleIds`.
 * Several may be picked at once (OR), "Sin ciclo" included. Each option shows a
 * contextual lead count (respecting the other active list filters, including
 * product). Renders nothing when the active catalog has no cycles.
 */
export function CycleFilterSelect(): React.JSX.Element | null {
  const { data: catalog } = useCatalogContext();
  const cycleIds = useCrmStore((s) => s.cycleIds);
  const toggleCycle = useCrmStore((s) => s.toggleCycle);
  const clearCycles = useCrmStore((s) => s.clearCycles);

  // Other active list filters feed the contextual counts (never cycleIds itself).
  const stages = useCrmStore((s) => s.stages);
  const channel = useCrmStore((s) => s.channel);
  const scoreMin = useCrmStore((s) => s.scoreMin);
  const scoreMax = useCrmStore((s) => s.scoreMax);
  const productIds = useCrmStore((s) => s.productIds);
  const search = useCrmStore((s) => s.search);
  const from = useCrmStore((s) => s.from);
  const to = useCrmStore((s) => s.to);
  const labelIds = useCrmStore((s) => s.labelIds);
  const assigneeIds = useCrmStore((s) => s.assigneeIds);

  const { data: countsData } = useCycleLeadCounts({
    stages: stages.join(","),
    channel,
    scoreMin,
    scoreMax,
    productIds: productIds.join(","),
    search,
    from,
    to,
    labelIds: labelIds.join(","),
    assigneeIds: assigneeIds.join(","),
  });

  const cycles = catalog?.cycles ?? [];
  if (cycles.length === 0) return null;

  const activeIds = new Set(cycles.map((c) => c.id));
  const countByCycle = new Map<string, number>();
  let noCycleCount = 0;
  // Cycles that hold leads but are no longer in the active catalog — see the
  // same note in ProductFilterSelect.
  const archived: Array<{ id: string; label: string; count: number }> = [];

  for (const c of countsData?.counts ?? []) {
    if (c.cycleId === null) {
      noCycleCount = c.count;
      continue;
    }
    countByCycle.set(c.cycleId, c.count);
    if (!activeIds.has(c.cycleId) && c.count > 0) {
      archived.push({ id: c.cycleId, label: c.label ?? "Ciclo eliminado", count: c.count });
    }
  }
  archived.sort((a, b) => b.count - a.count);

  return (
    <MultiSelectFilter
      options={[
        ...cycles.map((c) => ({ id: c.id, name: c.label, count: countByCycle.get(c.id) ?? 0 })),
        ...archived.map((c) => ({ id: c.id, name: `${c.label} (archivado)`, count: c.count })),
        { id: NO_CYCLE, name: "Sin ciclo", count: noCycleCount },
      ]}
      selected={cycleIds}
      onToggle={toggleCycle}
      onClear={clearCycles}
      placeholder="Todos los ciclos"
      pluralNoun="ciclos"
      singularNoun="ciclo"
      clearLabel="Todos los ciclos"
      ariaLabel="Ciclo"
      contentClassName="w-auto min-w-56 max-w-[min(28rem,calc(100vw-2rem))]"
    />
  );
}
